import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '@shop/api-client'
import Categories from './screens/Categories'
import ChangePassword from './screens/ChangePassword'
import Commissions from './screens/Commissions'
import Dashboard from './screens/Dashboard'
import DraftReview from './screens/DraftReview'
import Drafts from './screens/Drafts'
import IntakeNew from './screens/IntakeNew'
import Login from './screens/Login'
import Opening from './screens/Opening'
import ProductDetail from './screens/ProductDetail'
import ProductNew from './screens/ProductNew'
import Accounting from './screens/Accounting'
import Adjustments, { AdjustmentDetailScreen } from './screens/Adjustments'
import PhaseTwoBoard from './screens/PhaseTwoBoard'
import Purchases, { PurchaseDetailScreen } from './screens/Purchases'
import StockInquiry from './screens/StockInquiry'
import SupplierReturns, { SupplierReturnDetailScreen } from './screens/SupplierReturns'
import Transfers, { TransferDetailScreen } from './screens/Transfers'
import Receipts, { ReceiptDetailScreen } from './screens/Receipts'
import Receivables from './screens/Receivables'
import Products from './screens/Products'
import Publishing from './screens/Publishing'
import Uncosted from './screens/Uncosted'
import PurchaseInvoice from './screens/PurchaseInvoice'
import CashBook from './screens/CashBook'
import Balances from './screens/Balances'
import Statement from './screens/Statement'
import Attendance from './screens/Attendance'
import OfflineOps from './screens/OfflineOps'
import WindowsApp from './screens/WindowsApp'
import Payroll from './screens/Payroll'
import PurchaseBills from './screens/PurchaseBills'
import ItemMovement from './screens/ItemMovement'
import WebOrders from './screens/WebOrders'
import Offers from './screens/Offers'
import Social from './screens/Social'
import SocialCompose from './screens/SocialCompose'
import CashierSales from './screens/CashierSales'
import Reports from './screens/Reports'
import Roles from './screens/Roles'
import SalesSettings from './screens/SalesSettings'
import Staff from './screens/Staff'
import Stocktake from './screens/Stocktake'
import Suppliers from './screens/Suppliers'
import Warehouses from './screens/Warehouses'
import StaffDetail from './screens/StaffDetail'
import Shell from './ui/Shell'
import { Spinner } from '@shop/ui'

/**
 * مسارُ النسخة الأولى: <b>دخول ⇐ إدخالٌ ذكيّ ⇐ مراجعة ⇐ اعتماد ⇐ صنفٌ وسعرٌ
 * ورصيدٌ افتتاحيّ ⇐ مسوّداتي ولوحة</b>. لا شاشةَ خارج هذا.
 *
 * <b>وثلاثُ حالاتٍ لا حالتان</b>: «لم يُسأل الخادمُ بعد» ليست «لا جلسة». ولو
 * سُوّي بينهما لَومض بابُ الدخول في وجه كلّ موظّفٍ يحدّث الصفحة، ولَخرج من
 * صفحته إلى الجذر بعد أن يعود التدويرُ بنجاح.
 */
export default function App() {
  const { session } = useAuth()

  if (session === undefined) {
    return <Spinner label="يُستأنف…" />
  }

  if (session === null) {
    return <Login />
  }

  // ولا طريقَ إلى الشاشات قبل تغيير الكلمة المؤقّتة: البوّابةُ هنا لا في كلّ
  // شاشة، فلا تُنسى في شاشةٍ تُضاف غدًا.
  if (session.mustChangePassword) {
    return <ChangePassword forced />
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route path="intake" element={<IntakeNew />} />
        <Route path="drafts" element={<Drafts />} />
        <Route path="drafts/:id" element={<DraftReview />} />
        <Route path="products" element={<Products />} />
        <Route path="publishing" element={<Publishing />} />
        <Route path="uncosted" element={<Uncosted />} />
        <Route path="purchase-invoice" element={<PurchaseInvoice />} />
        <Route path="cashbook" element={<CashBook />} />
        <Route path="balances" element={<Balances />} />
        <Route path="statement" element={<Statement />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="offline-ops" element={<OfflineOps />} />
        <Route path="windows-app" element={<WindowsApp />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="purchase-bills" element={<PurchaseBills />} />
        <Route path="purchase-bills/:id" element={<PurchaseBills />} />
        <Route path="purchase-bills/cost/:id" element={<PurchaseBills kind="cost" />} />
        <Route path="item-movement" element={<ItemMovement />} />
        <Route path="web-orders" element={<WebOrders />} />
        <Route path="offers" element={<Offers />} />
        <Route path="social" element={<Social />} />
        <Route path="social/new" element={<SocialCompose />} />
        <Route path="cashier-sales" element={<CashierSales />} />

        {/* ‏`new` قبل `:id` في القراءة لا في الترتيب: React Router 6 يرتّب
            بالتخصيص فالثابتُ يسبق المتغيّرَ مهما كُتب. وهو مكتوبٌ هكذا ليُقرأ. */}
        <Route path="products/new" element={<ProductNew />} />
        <Route path="products/:id" element={<ProductDetail />} />
        <Route path="stocktake" element={<Stocktake />} />
        <Route path="opening" element={<Opening />} />
        <Route path="warehouses" element={<Warehouses />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="purchases/:id" element={<PurchaseDetailScreen />} />
        <Route path="receipts" element={<Receipts />} />
        <Route path="receipts/:id" element={<ReceiptDetailScreen />} />
        <Route path="supplier-returns" element={<SupplierReturns />} />
        <Route path="supplier-returns/:id" element={<SupplierReturnDetailScreen />} />
        <Route path="adjustments" element={<Adjustments />} />
        <Route path="adjustments/:id" element={<AdjustmentDetailScreen />} />
        <Route path="transfers" element={<Transfers />} />
        <Route path="transfers/:id" element={<TransferDetailScreen />} />
        <Route path="stock" element={<StockInquiry />} />
        <Route path="board" element={<PhaseTwoBoard />} />
        <Route path="reports" element={<Reports />} />
        <Route path="receivables" element={<Receivables />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="commissions" element={<Commissions />} />
        <Route path="categories" element={<Categories />} />
        <Route path="staff" element={<Staff />} />
        <Route path="roles" element={<Roles />} />
        <Route path="sales-settings" element={<SalesSettings />} />
        <Route path="staff/:id" element={<StaffDetail />} />
        <Route path="password" element={<ChangePassword forced={false} />} />

        {/* مسارٌ مجهولٌ يعود إلى اللوحة: عنوانٌ قديمٌ في المفضّلة لا يترك
            الموظّفَ أمام صفحةٍ بيضاء. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
